/** Convert common video URLs (YouTube, Vimeo, direct files) to an embeddable src. */
export function getVideoEmbedUrl(rawUrl: string): string | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    const host = parsed.hostname.replace(/^www\./, '');

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const videoId = parsed.searchParams.get('v');
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
      const embedMatch = parsed.pathname.match(/^\/embed\/([^/?]+)/);
      if (embedMatch?.[1]) return `https://www.youtube.com/embed/${embedMatch[1]}`;
    }

    if (host === 'youtu.be') {
      const videoId = parsed.pathname.replace(/^\//, '').split('/')[0];
      if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    }

    if (host === 'vimeo.com') {
      const videoId = parsed.pathname.replace(/^\//, '').split('/')[0];
      if (videoId && /^\d+$/.test(videoId)) return `https://player.vimeo.com/video/${videoId}`;
    }

    if (host === 'player.vimeo.com') {
      const videoId = parsed.pathname.match(/^\/video\/(\d+)/)?.[1];
      if (videoId) return `https://player.vimeo.com/video/${videoId}`;
    }

    if (isDirectVideoFileUrl(parsed.href)) return parsed.href;
  } catch {
    return null;
  }

  return null;
}

/** YouTube / Vimeo thumbnail for preview tiles (not company slide). */
export function getVideoThumbnailUrl(rawUrl: string): string | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
    const host = parsed.hostname.replace(/^www\./, '');

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const videoId = parsed.searchParams.get('v');
      if (videoId) return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      const embedMatch = parsed.pathname.match(/^\/embed\/([^/?]+)/);
      if (embedMatch?.[1]) return `https://img.youtube.com/vi/${embedMatch[1]}/hqdefault.jpg`;
    }

    if (host === 'youtu.be') {
      const videoId = parsed.pathname.replace(/^\//, '').split('/')[0];
      if (videoId) return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }

    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const videoId =
        parsed.pathname.match(/^\/video\/(\d+)/)?.[1] ||
        parsed.pathname.replace(/^\//, '').split('/')[0];
      if (videoId && /^\d+$/.test(videoId)) {
        return `https://vumbnail.com/${videoId}.jpg`;
      }
    }
  } catch {
    return null;
  }

  return null;
}

export function isDirectVideoFileUrl(url: string): boolean {
  return /\.(mp4|webm|ogg)(\?.*)?$/i.test(url);
}

/** Preview image for “Watch video pitch” — video platform thumb first, company slide as fallback. */
export function getVideoPitchPreviewUrl(
  videoUrl: string,
  companyThumbnailUrl?: string
): string | null {
  return getVideoThumbnailUrl(videoUrl) || companyThumbnailUrl?.trim() || null;
}
